/**
 * Admin — Konten-Werkzeuge (Jul-2026, User-Wunsch).
 *
 * 1. Konten zusammenführen: doppelte Konten (z. B. Vermittler + Händler) zu
 *    einem Konto vereinen — Referenzen wandern zum Ziel, die Quell-E-Mail
 *    wird gelöscht und für neue Registrierungen frei.
 * 2. DSGVO-Löschanträge (Art. 17) abarbeiten: Konto anonymisieren, Login
 *    sperren, E-Mail freigeben (Archiv 30 Tage, GoBD-Belege bleiben).
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { GitMerge, Trash2, Loader2, ShieldCheck, RefreshCw, Archive, Sparkles, MapPin } from "lucide-react";
import api from "../lib/api";

// Aug-2026 (User-Wunsch) — Test-Verkäufe eines Standorts sicher zurücksetzen.
function TestSalesCleanupSection() {
    const [dealers, setDealers] = useState([]);
    const [dealerId, setDealerId] = useState("");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/admin/test-data/sales").then((r) => setDealers(r.data.dealers || [])).catch(() => {});
    }, []);
    const loadPreview = async (id) => {
        setDealerId(id); setPreview(null);
        if (!id) return;
        try {
            const { data } = await api.get(`/admin/test-data/sales?dealer_id=${id}`);
            setPreview(data);
        } catch (e) { toast.error(e.response?.data?.detail || "Vorschau fehlgeschlagen"); }
    };
    const cleanup = async () => {
        const n = preview?.sold_listings?.length || 0;
        const m = preview?.invoices?.length || 0;
        if (!window.confirm(`Wirklich bereinigen?\n\n${n} Fahrzeug(e) werden auf „nicht verkauft" zurückgesetzt und ${m} verknüpfte Rechnung(en) in den Papierkorb verschoben (mit Backup).`)) return;
        setBusy(true);
        try {
            const { data } = await api.post("/admin/test-data/sales/cleanup", { dealer_id: dealerId });
            toast.success(`Bereinigt: ${data.listings_reset} Fahrzeuge zurückgesetzt, ${data.invoices_trashed} Rechnung(en) in den Papierkorb`);
            loadPreview(dealerId);
        } catch (e) { toast.error(e.response?.data?.detail || "Bereinigung fehlgeschlagen"); }
        setBusy(false);
    };
    return (
        <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="admin-test-sales-section">
            <h2 className="font-black text-base text-[#111827] flex items-center gap-2 mb-1"><Trash2 className="w-4 h-4" /> Test-Verkäufe bereinigen</h2>
            <p className="text-xs text-gray-500 mb-3">Setzt alle als verkauft/fakturiert markierten Fahrzeuge eines Standorts zurück und verschiebt verknüpfte Rechnungen mit Backup in den Papierkorb. Für Testdaten nach dem Go-Live.</p>
            <select value={dealerId} onChange={(e) => loadPreview(e.target.value)}
                className="border border-gray-300 rounded-sm px-3 py-2 text-sm w-full max-w-md" data-testid="test-sales-dealer-select">
                <option value="">— Standort wählen —</option>
                {dealers.map((d) => (
                    <option key={d.id} value={d.id}>{d.company_name || d.email}{d.site_code ? ` · ${d.site_code}` : ""}{d.city ? ` (${d.city})` : ""}</option>
                ))}
            </select>
            {preview && (
                <div className="mt-3" data-testid="test-sales-preview">
                    {(preview.sold_listings || []).length === 0 ? (
                        <div className="text-sm text-gray-400" data-testid="test-sales-empty">Keine verkauften/fakturierten Fahrzeuge an diesem Standort.</div>
                    ) : (
                        <>
                            <div className="text-xs font-bold text-gray-600 uppercase tracking-wider mb-1">{preview.sold_listings.length} Verkauf/Verkäufe · {preview.invoices.length} verknüpfte Rechnung(en)</div>
                            <div className="divide-y divide-gray-100 border border-gray-200 rounded-sm max-h-56 overflow-y-auto">
                                {preview.sold_listings.map((l) => (
                                    <div key={l.id} className="px-3 py-1.5 text-sm flex justify-between gap-2">
                                        <span className="truncate">{l.title}</span>
                                        <span className="text-gray-500 whitespace-nowrap">{l.price ? `${Number(l.price).toLocaleString("de-DE")} €` : ""}{l.sold ? " · verkauft" : ""}{l.invoiced ? " · fakturiert" : ""}</span>
                                    </div>
                                ))}
                                {preview.invoices.map((i) => (
                                    <div key={i.id} className="px-3 py-1.5 text-sm flex justify-between gap-2 bg-amber-50/40">
                                        <span>Rechnung {i.invoice_number}</span>
                                        <span className="text-gray-500">{i.status}</span>
                                    </div>
                                ))}
                            </div>
                            <button onClick={cleanup} disabled={busy}
                                className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-4 py-2 rounded-sm bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                                data-testid="test-sales-cleanup-btn">
                                {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Jetzt bereinigen
                            </button>
                        </>
                    )}
                </div>
            )}
        </section>
    );
}

// Jul-2026 (User-Wunsch) — „Aufräumer": doppelte Konten anzeigen + 1-Klick-Merge.
function DuplicateAccountsSection() {
    const [groups, setGroups] = useState(null);
    const [busy, setBusy] = useState("");
    const load = () => api.get("/admin/users/duplicates")
        .then((r) => setGroups(r.data.groups || [])).catch(() => setGroups([]));
    useEffect(() => { load(); }, []);

    const mergeInto = async (sourceId, targetId, label) => {
        if (!window.confirm(`Konto „${label}" in das Ziel-Konto zusammenführen?\n\nDas Quellkonto wird gelöscht, alle Referenzen wandern zum Ziel.`)) return;
        setBusy(sourceId);
        try {
            const { data } = await api.post("/admin/users/merge", { source: sourceId, target: targetId });
            toast.success(`Zusammengeführt — E-Mail ${data.source_email_freed} ist wieder frei.`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Zusammenführen fehlgeschlagen"); }
        setBusy("");
    };

    return (
        <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="admin-dup-accounts-section">
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-black text-base text-[#111827] flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-500" /> Aufräumer — doppelte Konten</h2>
                <button onClick={load} className="text-xs font-bold text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="dup-accounts-refresh">
                    <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
                </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Gefunden über gleiche E-Mail bzw. gleichen Namen + Firma. Das Konto mit den meisten Inseraten (★ Ziel) bleibt bestehen — die anderen per Klick zusammenführen.</p>
            {groups === null ? (
                <div className="text-sm text-gray-400 py-3" data-testid="dup-accounts-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Suche Duplikate …</div>
            ) : groups.length === 0 ? (
                <div className="text-sm text-gray-400" data-testid="dup-accounts-empty">Keine doppelten Konten gefunden. 🎉</div>
            ) : (
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                    {groups.map((g, gi) => (
                        <div key={g.key} className="border border-gray-200 rounded-sm" data-testid={`dup-group-${gi}`}>
                            <div className="px-3 py-1.5 bg-gray-50 text-[11px] font-bold text-gray-600 flex items-center justify-between">
                                <span>{g.reason}: {g.key}</span>
                                <span className="text-gray-400">{g.accounts.length} Konten</span>
                            </div>
                            <div className="divide-y divide-gray-100">
                                {g.accounts.map((a) => (
                                    <div key={a.id} className="flex items-center gap-3 px-3 py-2" data-testid={`dup-acc-${a.id}`}>
                                        <div className="min-w-0 flex-1">
                                            <div className="text-sm font-bold text-[#111827] truncate">
                                                {a.id === g.suggested_target && <span className="text-amber-500 mr-1" title="Vorgeschlagenes Ziel">★</span>}
                                                {a.name || "—"} · {a.email}
                                            </div>
                                            <div className="text-[11px] text-gray-500">
                                                {a.role} {a.company_name ? `· ${a.company_name}` : ""} · {a.listings_count} Inserate
                                                {a.is_branch && <span className="ml-1 text-sky-700 font-bold">· Filiale (Vorsicht!)</span>}
                                            </div>
                                        </div>
                                        {a.id !== g.suggested_target && (
                                            <button onClick={() => mergeInto(a.id, g.suggested_target, a.email || a.name)} disabled={busy === a.id}
                                                className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm border border-[#111827] bg-[#111827] text-[#FFCC00] hover:bg-black disabled:opacity-50"
                                                data-testid={`dup-merge-${a.id}`}>
                                                {busy === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5" />} In ★ zusammenführen
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

// Jul-2026 (User-Wunsch) — Standort-Bereinigung: doppelte Schreibweisen vereinheitlichen.
function LocationCleanupSection() {
    const [groups, setGroups] = useState(null);
    const [busy, setBusy] = useState("");
    const load = () => api.get("/admin/locations/duplicates")
        .then((r) => setGroups(r.data.groups || [])).catch(() => setGroups([]));
    useEffect(() => { load(); }, []);

    const merge = async (g, target) => {
        const variants = g.variants.map((v) => v.location).filter((l) => l !== target);
        if (!window.confirm(`Alle ${variants.length} Varianten auf „${target}" vereinheitlichen?\n\n${variants.join("\n")}`)) return;
        setBusy(g.normalized);
        try {
            const { data } = await api.post("/admin/locations/merge", { target, variants });
            toast.success(`${data.updated} Inserate auf „${target}" vereinheitlicht.`);
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || "Bereinigung fehlgeschlagen"); }
        setBusy("");
    };

    return (
        <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="admin-location-cleanup-section">
            <div className="flex items-center justify-between mb-1">
                <h2 className="font-black text-base text-[#111827] flex items-center gap-2"><MapPin className="w-4 h-4 text-sky-600" /> Aufräumer — Standort-Schreibweisen</h2>
                <button onClick={load} className="text-xs font-bold text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="location-cleanup-refresh">
                    <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
                </button>
            </div>
            <p className="text-xs text-gray-500 mb-3">Gleiche Standorte in unterschiedlicher Schreibweise (Str./Straße, Hausnummer, Groß/Klein) lassen Fahrzeuge aus Filtern verschwinden. Per Klick auf eine Variante wird sie als einheitliche Schreibweise übernommen.</p>
            {groups === null ? (
                <div className="text-sm text-gray-400 py-3" data-testid="location-cleanup-loading"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Analysiere Standorte …</div>
            ) : groups.length === 0 ? (
                <div className="text-sm text-gray-400" data-testid="location-cleanup-empty">Keine doppelten Standort-Schreibweisen gefunden. 🎉</div>
            ) : (
                <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
                    {groups.map((g, gi) => (
                        <div key={g.normalized} className="border border-gray-200 rounded-sm p-3" data-testid={`loc-group-${gi}`}>
                            <div className="text-[11px] font-bold text-gray-500 mb-2">
                                {g.variants.length} Schreibweisen · {g.total} Inserate — Klick = als einheitliche Schreibweise übernehmen:
                            </div>
                            <div className="flex flex-wrap gap-1.5">
                                {g.variants.map((v) => (
                                    <button key={v.location} onClick={() => merge(g, v.location)} disabled={busy === g.normalized}
                                        className={`text-xs px-2.5 py-1 rounded-sm border font-semibold disabled:opacity-50 transition-colors ${v.location === g.suggested_target ? "border-emerald-400 bg-emerald-50 text-emerald-800 hover:bg-emerald-100" : "border-gray-300 bg-gray-50 text-gray-700 hover:bg-gray-100"}`}
                                        title={v.location === g.suggested_target ? "Vorschlag (häufigste Variante)" : "Als Ziel-Schreibweise wählen"}
                                        data-testid={`loc-variant-${gi}-${v.location.replace(/[^a-zA-Z0-9]/g, "_")}`}>
                                        {v.location === g.suggested_target && "★ "}{v.location} ({v.count})
                                    </button>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </section>
    );
}

// Aug-2026 (User-Wunsch) — Zahnrad-Freigabe: welche Accounts sehen die
// Einstellungs-Zahnräder in der App (zusätzlich zu allen Admins).
function GearAccessSection() {
    const [emails, setEmails] = useState([]);
    const [input, setInput] = useState("");
    const [busy, setBusy] = useState(false);
    useEffect(() => {
        api.get("/admin/gear-access").then((r) => setEmails(r.data.emails || [])).catch(() => {});
    }, []);
    const save = async (next) => {
        setBusy(true);
        try {
            const r = await api.put("/admin/gear-access", { emails: next });
            setEmails(r.data.emails || []);
            toast.success("Zahnrad-Freigaben gespeichert. Nutzer sehen die Änderung nach dem nächsten Seiten-Neuladen.");
        } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen"); }
        setBusy(false);
    };
    const add = () => {
        const e = input.trim().toLowerCase();
        if (!e || !e.includes("@")) { toast.error("Bitte gültige E-Mail eingeben."); return; }
        if (emails.includes(e)) { toast.info("Bereits freigegeben."); return; }
        save([...emails, e]);
        setInput("");
    };
    return (
        <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="admin-gear-access-section">
            <h2 className="font-black text-base text-[#111827] mb-1">Zahnrad-Freigaben (Einstellungs-Buttons)</h2>
            <p className="text-xs text-gray-500 mb-3">
                Nur diese Accounts (plus alle Admins) sehen die Einstellungs-Zahnräder in der App —
                für alle anderen Nutzer sind sie verborgen.
            </p>
            <div className="flex flex-wrap gap-2 mb-3" data-testid="gear-access-list">
                {emails.map((e) => (
                    <span key={e} className="inline-flex items-center gap-1.5 bg-gray-100 border border-gray-200 rounded-sm px-2 py-1 text-xs font-bold" data-testid={`gear-email-${e}`}>
                        {e}
                        <button type="button" disabled={busy} onClick={() => save(emails.filter((x) => x !== e))}
                            className="text-gray-400 hover:text-red-600 font-black" title="Freigabe entfernen"
                            data-testid={`gear-email-remove-${e}`}>×</button>
                    </span>
                ))}
                {!emails.length && <span className="text-xs text-gray-400">Keine Freigaben — nur Admins sehen Zahnräder.</span>}
            </div>
            <div className="flex gap-2">
                <input value={input} onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && add()}
                    placeholder="email@beispiel.de"
                    className="flex-1 text-sm rounded-sm border border-gray-300 px-3 py-2 focus:border-[#111827] focus:outline-none"
                    data-testid="gear-access-input" />
                <button onClick={add} disabled={busy}
                    className="bg-[#111827] text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-black disabled:opacity-50"
                    data-testid="gear-access-add-btn">Freigeben</button>
            </div>
        </section>
    );
}

export default function AdminAccountToolsPage() {
    const [source, setSource] = useState("");
    const [target, setTarget] = useState("");
    const [merging, setMerging] = useState(false);
    const [result, setResult] = useState(null);
    const [requests, setRequests] = useState([]);
    const [busyId, setBusyId] = useState("");
    const [deleted, setDeleted] = useState([]);
    const [retention, setRetention] = useState([]);

    const loadRequests = () => api.get("/admin/deletion-requests")
        .then((r) => setRequests(r.data.items || [])).catch(() => {});
    const loadDeleted = () => api.get("/admin/deleted-accounts")
        .then((r) => setDeleted(r.data.items || [])).catch(() => {});
    const loadRetention = () => api.get("/admin/retention-register")
        .then((r) => setRetention(r.data.items || [])).catch(() => {});
    useEffect(() => { loadRequests(); loadDeleted(); loadRetention(); }, []);

    const restore = async (acc) => {
        if (!window.confirm(`Konto ${acc.email} wiederherstellen?`)) return;
        setBusyId(acc.id);
        try {
            const { data } = await api.post(`/admin/deleted-accounts/${acc.id}/restore`);
            toast.success(`Konto ${data.email} wiederhergestellt.`);
            loadDeleted(); loadRequests();
        } catch (e) { toast.error(e?.response?.data?.detail || "Wiederherstellung fehlgeschlagen"); }
        setBusyId("");
    };

    const merge = async () => {
        if (!source.trim() || !target.trim()) { toast.error("Bitte Quelle und Ziel angeben."); return; }
        if (!window.confirm(`Konto „${source.trim()}" in „${target.trim()}" zusammenführen?\n\nDas Quellkonto wird gelöscht, die E-Mail wird frei. Referenzen wandern zum Zielkonto.`)) return;
        setMerging(true);
        try {
            const { data } = await api.post("/admin/users/merge", { source: source.trim(), target: target.trim() });
            setResult(data);
            toast.success(`Zusammengeführt — E-Mail ${data.source_email_freed} ist wieder frei.`);
            setSource(""); setTarget("");
        } catch (e) { toast.error(e?.response?.data?.detail || "Zusammenführen fehlgeschlagen"); }
        setMerging(false);
    };

    const anonymize = async (req) => {
        if (!window.confirm(`Konto ${req.user?.email || req.user_id} endgültig anonymisieren?\n\nPersonenbezogene Daten werden entfernt, der Login gesperrt und die E-Mail freigegeben. Archiv-Kopie 30 Tage.`)) return;
        setBusyId(req.user_id);
        try {
            const { data } = await api.post(`/admin/users/${req.user_id}/anonymize`);
            toast.success(`Anonymisiert — E-Mail ${data.email_freed} ist wieder frei.`);
            loadRequests(); loadDeleted(); loadRetention();
        } catch (e) { toast.error(e?.response?.data?.detail || "Anonymisierung fehlgeschlagen"); }
        setBusyId("");
    };

    return (
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 space-y-8" data-testid="admin-account-tools-page">
            <div>
                <h1 className="font-display text-2xl font-black text-[#111827]">Konten-Werkzeuge</h1>
                <p className="text-sm text-gray-500 mt-1">Doppelte Konten zusammenführen, Standorte bereinigen und DSGVO-Löschanträge abarbeiten.</p>
            </div>

            <GearAccessSection />
            <TestSalesCleanupSection />
            <DuplicateAccountsSection />
            <LocationCleanupSection />

            <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="admin-merge-section">
                <h2 className="font-black text-base text-[#111827] flex items-center gap-2 mb-1"><GitMerge className="w-4 h-4" /> Konten zusammenführen</h2>
                <p className="text-xs text-gray-500 mb-4">Alle Daten/Referenzen des Quellkontos wandern zum Zielkonto. Das Quellkonto wird gelöscht — seine E-Mail ist danach wieder für Registrierungen frei.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Quelle (wird gelöscht)</label>
                        <input value={source} onChange={(e) => setSource(e.target.value)} placeholder="E-Mail oder Konto-ID"
                            className="mt-1 w-full text-sm rounded-sm border border-gray-300 px-3 py-2 focus:border-[#111827] focus:outline-none" data-testid="merge-source-input" />
                    </div>
                    <div>
                        <label className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Ziel (bleibt bestehen)</label>
                        <input value={target} onChange={(e) => setTarget(e.target.value)} placeholder="E-Mail oder Konto-ID"
                            className="mt-1 w-full text-sm rounded-sm border border-gray-300 px-3 py-2 focus:border-[#111827] focus:outline-none" data-testid="merge-target-input" />
                    </div>
                </div>
                <button onClick={merge} disabled={merging}
                    className="mt-4 inline-flex items-center gap-2 bg-[#111827] text-white rounded-sm px-4 py-2 text-sm font-bold hover:bg-black disabled:opacity-50" data-testid="merge-submit-btn">
                    {merging ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />} Zusammenführen
                </button>
                {result && (
                    <div className="mt-4 text-xs bg-emerald-50 border border-emerald-200 rounded-sm p-3" data-testid="merge-result">
                        <div className="font-bold text-emerald-800">E-Mail freigegeben: {result.source_email_freed}</div>
                        <div className="text-emerald-900 mt-1">Ziel: {result.target?.email} ({result.target?.role})</div>
                        {Object.keys(result.moved || {}).length > 0 && (
                            <div className="mt-1 text-gray-600">Verschoben: {Object.entries(result.moved).map(([k, v]) => `${k} (${v})`).join(", ")}</div>
                        )}
                    </div>
                )}
            </section>

            <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="admin-deletion-section">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-black text-base text-[#111827] flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Offene Löschanträge (DSGVO Art. 17)</h2>
                    <button onClick={loadRequests} className="text-xs font-bold text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="deletion-refresh-btn">
                        <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
                    </button>
                </div>
                {requests.length === 0 ? (
                    <div className="text-sm text-gray-400" data-testid="deletion-empty">Keine offenen Löschanträge.</div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {requests.map((r) => (
                            <div key={r.user_id + r.requested_at} className="flex items-center gap-3 py-2.5" data-testid={`deletion-req-${r.user_id}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-bold text-[#111827] truncate">{r.user?.name || "—"} · {r.user?.email || r.user_id}</div>
                                    <div className="text-[11px] text-gray-500">Rolle: {r.user?.role || "?"} · beantragt am {new Date(r.requested_at).toLocaleString("de-DE")}</div>
                                </div>
                                <button onClick={() => anonymize(r)} disabled={busyId === r.user_id}
                                    className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                                    data-testid={`deletion-anonymize-${r.user_id}`}>
                                    {busyId === r.user_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />} Anonymisieren &amp; abschließen
                                </button>
                            </div>
                        ))}
                    </div>
                )}
                <p className="text-[11px] text-gray-400 mt-3">Anonymisierung entfernt personenbezogene Daten, sperrt den Login und gibt die E-Mail frei. Der Nutzer erhält automatisch eine Lösch-Bestätigung per E-Mail. Archiv-Kopie 30 Tage (Wiederherstellung unten). <b>Daten mit gesetzlichen Aufbewahrungspflichten (z.&nbsp;B. Rechnungen &amp; Buchungsbelege, §&nbsp;147&nbsp;AO / §&nbsp;257&nbsp;HGB, 6–10 Jahre) bleiben für die Dauer der Frist erhalten</b> und sind für sonstige Verarbeitung gesperrt (Art. 17 Abs. 3 lit. b DSGVO).</p>
            </section>

            <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="admin-restore-section">
                <h2 className="font-black text-base text-[#111827] flex items-center gap-2 mb-1"><RefreshCw className="w-4 h-4" /> Gelöschte Konten — 30-Tage-Archiv</h2>
                <p className="text-xs text-gray-500 mb-3">Versehentlich gelöschte Konten können hier innerhalb von 30 Tagen per Klick vollständig wiederhergestellt werden (inkl. Login). Danach wird das Archiv endgültig entfernt.</p>
                {deleted.length === 0 ? (
                    <div className="text-sm text-gray-400" data-testid="restore-empty">Keine Konten im Archiv.</div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {deleted.map((a) => (
                            <div key={a.id} className="flex items-center gap-3 py-2.5" data-testid={`restore-row-${a.id}`}>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-bold text-[#111827] truncate">{a.name || "—"} · {a.email}</div>
                                    <div className="text-[11px] text-gray-500">Rolle: {a.role || "?"} · gelöscht am {new Date(a.deleted_at).toLocaleString("de-DE")} · endgültig weg am {new Date(a.purge_after).toLocaleDateString("de-DE")}</div>
                                    {(a.retention || []).length > 0 && (
                                        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-sm px-1.5 py-0.5 mt-1 inline-block" data-testid={`retention-${a.id}`}>
                                            Aufbewahrungspflichtig: {a.retention.map((x) => `${x.count}× ${x.label} — ${x.frist}`).join(" · ")}
                                        </div>
                                    )}
                                </div>
                                <button onClick={() => restore(a)} disabled={busyId === a.id}
                                    className="inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-sm border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                                    data-testid={`restore-btn-${a.id}`}>
                                    {busyId === a.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />} Wiederherstellen
                                </button>
                            </div>
                        ))}
                    </div>
                )}
            </section>

            <section className="bg-white border border-gray-200 rounded-sm p-5" data-testid="admin-retention-section">
                <div className="flex items-center justify-between mb-1">
                    <h2 className="font-black text-base text-[#111827] flex items-center gap-2"><Archive className="w-4 h-4" /> Aufbewahrungs-Register (§ 147 AO / § 257 HGB)</h2>
                    <button onClick={loadRetention} className="text-xs font-bold text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="retention-refresh-btn">
                        <RefreshCw className="w-3.5 h-3.5" /> Aktualisieren
                    </button>
                </div>
                <p className="text-xs text-gray-500 mb-3">Belege gelöschter Konten, die wegen gesetzlicher Aufbewahrungsfristen weiter vorgehalten werden (Art. 17 Abs. 3 lit. b DSGVO) — dauerhaft einsehbar, auch nach Ablauf des 30-Tage-Archivs.</p>
                {retention.length === 0 ? (
                    <div className="text-sm text-gray-400" data-testid="retention-empty">Keine aufbewahrungspflichtigen Belege gelöschter Konten.</div>
                ) : (
                    <div className="divide-y divide-gray-100">
                        {retention.map((r) => (
                            <div key={r.user_id} className="py-3" data-testid={`retention-row-${r.user_id}`}>
                                <div className="flex items-center gap-3 flex-wrap">
                                    <div className="text-sm font-bold text-[#111827] truncate">{r.name || "—"} · {r.email}</div>
                                    <span className="text-[10px] font-bold uppercase tracking-wider bg-gray-100 border border-gray-200 text-gray-600 rounded-sm px-1.5 py-0.5">{r.role || "?"}</span>
                                    {r.deleted_at && <span className="text-[11px] text-gray-500">gelöscht am {new Date(r.deleted_at).toLocaleDateString("de-DE")}</span>}
                                    {r.keep_until && (
                                        <span className={`text-[11px] font-bold rounded-sm px-1.5 py-0.5 border ${r.expired ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-amber-50 border-amber-200 text-amber-800"}`} data-testid={`retention-until-${r.user_id}`}>
                                            {r.expired ? "Frist abgelaufen — Löschung möglich" : `aufbewahren bis ${new Date(r.keep_until).toLocaleDateString("de-DE")}`}
                                        </span>
                                    )}
                                </div>
                                <div className="mt-1.5 flex flex-wrap gap-1.5">
                                    {(r.documents || []).map((d) => (
                                        <span key={d.label} className="text-[11px] bg-gray-50 border border-gray-200 rounded-sm px-2 py-0.5 text-gray-700">
                                            <b>{d.count}×</b> {d.label} · {d.years} Jahre{d.last_date ? ` · letzter Beleg ${new Date(d.last_date).toLocaleDateString("de-DE")}` : ""} · bis {new Date(d.keep_until).toLocaleDateString("de-DE")}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
}
